import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { ConfirmModal } from 'components/ConfirmModal';
import { Icon } from 'components/Icon';
import { Input } from 'components/Input';
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context';
import { Snackbar } from 'components/Snackbar';
import { Textarea } from 'components/Textarea';
import { pageCardClasses } from 'components/AppLayout/styles';
import {
  DEFAULT_FRIEND_LIMIT,
  MEMO_MAX_LENGTH,
  MIN_FRIEND_COUNT,
} from 'constants/index';
import { routes } from 'constants/routes';
import { useOrgPath } from 'hooks/useOrgPath';
import { commonStrings, lineStrings } from 'constants/strings';
import {
  useLineChannelAccount,
  useReissueLineChannelAccessToken,
  useUnlinkLineChannel,
  useUpdateLineChannel,
  useVerifyLineChannelWebhook,
} from 'hooks';
import { ELineChannelStatus, ELineWebhookStatus, TUpdateLineChannelRequest } from 'types';
import { getWebhookStatusLabel, getWebhookStatusStyle } from '../LineAccountsTable';

const DEFAULT_LINE_CHANNEL_STATUS = ELineChannelStatus.ACTIVE;

const normalizeBasicId = (value: string) => value.trim().replace(/^@+/, '');
const normalizeOptionalMemo = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};
const parseOptionalNonNegativeInt = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return { value: DEFAULT_FRIEND_LIMIT, isValid: true };
  const parsed = Number(trimmed);
  if (
    !Number.isFinite(parsed) ||
    !Number.isInteger(parsed) ||
    parsed < MIN_FRIEND_COUNT
  ) {
    return { value: DEFAULT_FRIEND_LIMIT, isValid: false };
  }
  return { value: parsed, isValid: true };
};

export default function LineEdit() {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const { lineChannelId } = useParams();
  const strings = lineStrings.edit;
  const lineChannelIdNumber = Number(lineChannelId);
  const hasValidId =
    Number.isFinite(lineChannelIdNumber) && lineChannelIdNumber > 0;
  const { account, loading, error } = useLineChannelAccount(
    hasValidId ? lineChannelIdNumber : undefined,
  );

  const [basicId, setBasicId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [channelSecret, setChannelSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [friendLimit, setFriendLimit] = useState(String(DEFAULT_FRIEND_LIMIT));
  const [status, setStatus] = useState<ELineChannelStatus>(
    DEFAULT_LINE_CHANNEL_STATUS,
  );
  const [activeFriends, setActiveFriends] = useState(MIN_FRIEND_COUNT);
  const [isTokenVisible, setIsTokenVisible] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success'>('idle');
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isReissueSnackbarOpen, setIsReissueSnackbarOpen] = useState(false);
  const [verifySnackbar, setVerifySnackbar] = useState<{ open: boolean; success: boolean; detail?: string | null }>({ open: false, success: false });
  const [webhookStatus, setWebhookStatus] = useState<ELineWebhookStatus>(ELineWebhookStatus.UNVERIFIED);
  const [webhookMessage, setWebhookMessage] = useState<string | undefined>();
  const [memo, setMemo] = useState('');
  const [validationError, setValidationError] = useState<string | undefined>();
  const [isVerifyConfirmOpen, setIsVerifyConfirmOpen] = useState(false);
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);
  const markSaved = useUnsavedChangesGuard(
    JSON.stringify({ basicId, channelId, channelSecret, friendLimit, status, memo }),
  );
  const {
    reissue,
    isSubmitting: isReissuing,
    error: reissueError,
  } = useReissueLineChannelAccessToken({
    onSuccess: (nextToken) => {
      setAccessToken(nextToken);
      setCopyStatus('idle');
      setIsReissueSnackbarOpen(true);
    },
  });
  const {
    update,
    isSubmitting: isUpdating,
    error: updateError,
  } = useUpdateLineChannel({
    onSuccess: (response) => {
      if (response.webhookMessage) {
        setWebhookMessage(response.webhookMessage);
      }
      markSaved();
      navigate(orgPathFor(routes.LINE), {
        state: { snackbarMessage: commonStrings.actions.updateSuccess },
      });
    },
  });
  const {
    unlink,
    isSubmitting: isUnlinking,
    error: unlinkError,
    resetError: resetUnlinkError,
  } = useUnlinkLineChannel({
    onSuccess: () => {
      setIsUnlinkConfirmOpen(false);
      navigate(orgPathFor(routes.LINE));
    },
  });
  const {
    verify,
    isSubmitting: isVerifying,
    error: verifyError,
    resetError: resetVerifyError,
  } = useVerifyLineChannelWebhook({
    onSuccess: (response) => {
      const nextStatus = response.success
        ? ELineWebhookStatus.VERIFIED
        : ELineWebhookStatus.INVALID;
      setWebhookStatus(nextStatus);
      setIsVerifyConfirmOpen(false);
      setVerifySnackbar({ open: true, success: response.success, detail: response.detail });
    },
  });

  // 取得したアカウントでフォームを同期し、消えた場合はリセットする。
  useEffect(() => {
    const resetCopyTimer = () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
        copyResetTimerRef.current = null;
      }
    };

    if (account) {
      resetCopyTimer();
      setBasicId(normalizeBasicId(account.basicId));
      setChannelId(account.channelId);
      setChannelSecret(account.channelSecret);
      setAccessToken(account.accessToken);
      setFriendLimit(String(account.friendLimit ?? DEFAULT_FRIEND_LIMIT));
      setStatus(account.status ?? DEFAULT_LINE_CHANNEL_STATUS);
      setActiveFriends(account.activeFriends ?? MIN_FRIEND_COUNT);
      setIsTokenVisible(false);
      setCopyStatus('idle');
      setIsReissueSnackbarOpen(false);
      setWebhookStatus(account.webhookStatus ?? ELineWebhookStatus.UNVERIFIED);
      setMemo(account.memo ?? '');
      markSaved();
      return;
    }
    resetCopyTimer();
    setBasicId('');
    setChannelId('');
    setChannelSecret('');
    setAccessToken('');
    setFriendLimit(String(DEFAULT_FRIEND_LIMIT));
    setStatus(DEFAULT_LINE_CHANNEL_STATUS);
    setActiveFriends(MIN_FRIEND_COUNT);
    setIsTokenVisible(false);
    setCopyStatus('idle');
    setIsReissueSnackbarOpen(false);
    setMemo('');
    markSaved();
  }, [account, markSaved]);

  const isFormDisabled = loading || !account;

  const notFound = !loading && !error && (!hasValidId || !account);
  const alertMessage =
    validationError ||
    webhookMessage ||
    verifyError ||
    unlinkError ||
    updateError ||
    error ||
    (notFound ? strings.states.notFound : undefined);

  const webhookAlertMessage =
    webhookStatus === ELineWebhookStatus.UNVERIFIED
      ? strings.webhookAlert.unverified
      : webhookStatus === ELineWebhookStatus.INVALID
        ? strings.webhookAlert.invalid
        : undefined;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isFormDisabled || isUpdating || !hasValidId) return;

    setValidationError(undefined);
    setWebhookMessage(undefined);
    resetVerifyError();
    resetUnlinkError();

    const normalizedBasicId = normalizeBasicId(basicId);
    const trimmedChannelId = channelId.trim();
    const trimmedChannelSecret = channelSecret.trim();
    const friendLimitResult = parseOptionalNonNegativeInt(friendLimit);

    if (!normalizedBasicId || !trimmedChannelId || !trimmedChannelSecret) {
      setValidationError(strings.errors.required);
      return;
    }

    if (!friendLimitResult.isValid) {
      setValidationError(strings.errors.invalidFriendLimit);
      return;
    }

    const request: TUpdateLineChannelRequest = {
      basicId: normalizedBasicId,
      channelId: trimmedChannelId,
      channelSecret: trimmedChannelSecret,
      friendLimit: friendLimitResult.value,
      status,
      memo: normalizeOptionalMemo(memo),
    };

    update(lineChannelIdNumber, request);
  };

  const handleCopyToken = async () => {
    if (!accessToken) return;
    try {
      await navigator.clipboard.writeText(accessToken);
      setCopyStatus('success');
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
      copyResetTimerRef.current = setTimeout(() => {
        setCopyStatus('idle');
      }, 2000);
    } catch { }
  };

  const handleReissueToken = () => {
    if (isFormDisabled || isReissuing || !hasValidId) return;

    const trimmedChannelSecret = channelSecret.trim();
    if (!trimmedChannelSecret) {
      setValidationError(strings.errors.required);
      return;
    }

    setValidationError(undefined);
    setWebhookMessage(undefined);
    resetVerifyError();
    resetUnlinkError();
    setIsReissueSnackbarOpen(false);
    reissue(lineChannelIdNumber, { channelSecret: trimmedChannelSecret });
  };

  const handleRequestVerify = () => {
    if (isFormDisabled || isVerifying || !hasValidId) return;
    resetVerifyError();
    setVerifySnackbar((prev) => ({ ...prev, open: false }));
    setIsVerifyConfirmOpen(true);
  };

  const handleConfirmVerify = () => {
    if (!hasValidId || isVerifying) return;
    verify(lineChannelIdNumber);
  };

  const handleRequestUnlink = () => {
    if (isFormDisabled || isUnlinking) return;
    setIsUnlinkConfirmOpen(true);
  };

  const handleConfirmUnlink = () => {
    if (!hasValidId || isUnlinking) return;
    unlink(lineChannelIdNumber);
  };

  // WebKit は連続する記号(*)を折り返さないため、狭幅でも1行に収まる長さに固定する
  const maskedAccessToken = accessToken ? '*'.repeat(24) : '';

  useEffect(() => {
    if (!reissueError) return;
    setIsReissueSnackbarOpen(true);
  }, [reissueError]);

  useEffect(() => {
    if (!verifyError) return;
    setIsVerifyConfirmOpen(false);
  }, [verifyError]);

  useEffect(
    () => () => {
      if (copyResetTimerRef.current) {
        clearTimeout(copyResetTimerRef.current);
      }
    },
    [],
  );

  return (
    <>
      <section className={`mx-auto w-full max-w-[980px] ${pageCardClasses.large}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted">
              {strings.kicker}
            </span>
            <h1 className="text-2xl font-display text-ink">{strings.title}</h1>
            <p className="text-sm text-muted">{strings.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => navigate(orgPathFor(routes.LINE))}
            >
              {commonStrings.actions.backToList}
            </Button>
          </div>
        </header>

        {alertMessage && (
          <div className="mt-6">
            <Alert>{alertMessage}</Alert>
          </div>
        )}

        {!loading && account && webhookAlertMessage && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
            <p className="text-sm text-amber-800">{webhookAlertMessage}</p>
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          {lineStrings.providerNotice.map((line, i) => (
            <p key={i} className="text-sm text-amber-800">{line}</p>
          ))}
        </div>

        <div className="mt-6 grid gap-4">
          {/* カード1: チャネル情報の編集 */}
          <form
            className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
            onSubmit={handleSubmit}
          >
            <div className="grid gap-1">
              <h2 className="text-sm font-semibold text-ink">
                {strings.form.title}
              </h2>
              <p className="text-xs text-muted">{strings.form.description}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="grid gap-2 text-xs font-semibold text-ink">
                {strings.form.fields.basicId.label}
                <div className="flex items-stretch">
                  <span className="inline-flex items-center rounded-l-2xl border border-border/80 border-r-0 bg-white/80 px-3 text-sm text-muted/70 shadow-sm">
                    @
                  </span>
                  <Input
                    name="basicId"
                    placeholder={strings.form.fields.basicId.placeholder}
                    autoComplete="off"
                    className="rounded-l-none border-l-0"
                    required
                    disabled={isFormDisabled}
                    value={basicId}
                    onChange={(event) => setBasicId(event.target.value)}
                  />
                </div>
              </label>
              <label className="grid gap-2 text-xs font-semibold text-ink">
                {strings.form.fields.channelId.label}
                <Input
                  name="channelId"
                  placeholder={strings.form.fields.channelId.placeholder}
                  autoComplete="off"
                  required
                  disabled={isFormDisabled}
                  value={channelId}
                  onChange={(event) => setChannelId(event.target.value)}
                />
              </label>
              <label className="grid gap-2 text-xs font-semibold text-ink sm:col-span-2">
                {strings.form.fields.channelSecret.label}
                {/* 長いシークレットを途中で見切れさせず全文を折り返し表示する（編集可能のまま） */}
                <textarea
                  name="channelSecret"
                  placeholder={strings.form.fields.channelSecret.placeholder}
                  autoComplete="off"
                  required
                  rows={2}
                  disabled={isFormDisabled}
                  value={channelSecret}
                  onChange={(event) => setChannelSecret(event.target.value)}
                  className="w-full resize-none break-all rounded-2xl border border-border/80 bg-white/80 px-3 py-2.5 font-mono text-sm leading-relaxed text-ink placeholder:font-sans placeholder:text-muted/70 shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-70"
                />
              </label>
              <label className="grid gap-2 text-xs font-semibold text-ink">
                {strings.form.fields.friendLimit.label}
                <Input
                  type="number"
                  min={0}
                  step={1}
                  name="friendLimit"
                  placeholder={strings.form.fields.friendLimit.placeholder}
                  autoComplete="off"
                  disabled={isFormDisabled}
                  value={friendLimit}
                  onChange={(event) => setFriendLimit(event.target.value)}
                />
              </label>
              <label className="grid gap-2 text-xs font-semibold text-ink">
                {strings.form.fields.activeFriends.label}
                <Input
                  name="activeFriends"
                  disabled
                  value={String(activeFriends)}
                />
              </label>
              <div className="grid gap-2 text-xs font-semibold text-ink">
                <span>{strings.form.fields.status.label}</span>
                <select
                  name="status"
                  className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                  disabled={isFormDisabled}
                  value={status}
                  onChange={(event) =>
                    setStatus(event.target.value as ELineChannelStatus)
                  }
                >
                  <option value={ELineChannelStatus.ACTIVE}>
                    {strings.statusOptions.active}
                  </option>
                  <option value={ELineChannelStatus.PAUSED}>
                    {strings.statusOptions.paused}
                  </option>
                  <option value={ELineChannelStatus.FULL}>
                    {strings.statusOptions.full}
                  </option>
                </select>
              </div>
              <label className="grid gap-2 text-xs font-semibold text-ink sm:col-span-2">
                {strings.form.fields.memo.label}
                <Textarea
                  name="memo"
                  rows={4}
                  placeholder={strings.form.fields.memo.placeholder}
                  maxLength={MEMO_MAX_LENGTH}
                  disabled={isFormDisabled}
                  value={memo}
                  onChange={(event) => setMemo(event.target.value)}
                />
                <span className="text-[11px] font-medium text-muted">
                  {strings.form.fields.memo.helper} ({memo.length}/
                  {MEMO_MAX_LENGTH})
                </span>
              </label>
            </div>
            <div className="flex justify-end">
              <Button
                size="md"
                type="submit"
                disabled={isFormDisabled || isUpdating || isUnlinking}
              >
                {commonStrings.actions.update}
              </Button>
            </div>
          </form>

          {/* カード2: Webhook・ステータス情報 */}
          <div className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-ink">
              {strings.form.sections.statusInfo}
            </h2>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-xs font-semibold text-ink">
                <span>{strings.form.fields.webhookStatus.label}</span>
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getWebhookStatusStyle(webhookStatus)}`}>
                  {getWebhookStatusLabel(webhookStatus)}
                </span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="primary"
                disabled={isFormDisabled || isVerifying}
                onClick={handleRequestVerify}
              >
                {lineStrings.management.actions.verify}
              </Button>
            </div>
          </div>

          {/* カード3: アクセストークン */}
          <div className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-ink">
              {strings.form.fields.accessToken.label}
            </h2>
            <div className="grid gap-2 text-xs font-semibold text-ink">
              <div className="flex flex-wrap items-center justify-end gap-2">
                {copyStatus === 'success' && (
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-muted">
                    <Icon icon="check" className="text-accent" />
                    {commonStrings.actions.copySuccess}
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isFormDisabled || !accessToken}
                  onClick={() => setIsTokenVisible((value) => !value)}
                >
                  {isTokenVisible
                    ? commonStrings.actions.hide
                    : commonStrings.actions.show}
                </Button>
              </div>
              <button
                type="button"
                className="w-full min-w-0 rounded-2xl bg-white/80 px-3 py-2 text-left text-[11px] font-mono text-ink shadow-sm whitespace-normal break-all [overflow-wrap:anywhere] transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={handleCopyToken}
                disabled={isFormDisabled || !accessToken}
                aria-label={commonStrings.actions.copy}
                title={commonStrings.actions.copy}
              >
                {isTokenVisible ? accessToken : maskedAccessToken}
              </button>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={isFormDisabled || isReissuing}
                  onClick={handleReissueToken}
                >
                  {strings.form.actions.reissueToken}
                </Button>
              </div>
            </div>
          </div>

          {/* カード4: デンジャーゾーン */}
          <div className="rounded-2xl border border-red-200 bg-red-50/50 p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-red-600/80">
                {lineStrings.management.confirm.unlinkDescription}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0 border-[#F35D5D] bg-[#F35D5D] text-white shadow-[0_16px_30px_rgba(243,93,93,0.32)] hover:border-[#E34E4E] hover:bg-[#E34E4E] hover:text-white hover:shadow-[0_20px_40px_rgba(243,93,93,0.38)] disabled:shadow-none"
                disabled={isFormDisabled || isUnlinking}
                onClick={handleRequestUnlink}
              >
                {lineStrings.management.actions.unlink}
              </Button>
            </div>
          </div>
        </div>
      </section>
      <ConfirmModal
        open={isVerifyConfirmOpen}
        textTitle={lineStrings.management.confirm.verifyTitle}
        textMessage={`@${normalizeBasicId(basicId)}\n${lineStrings.management.confirm.verifyDescription}`}
        textCancel={commonStrings.cancel}
        textConfirm={lineStrings.management.confirm.verifyConfirm}
        onClose={() => {
          if (isVerifying) return;
          setIsVerifyConfirmOpen(false);
        }}
        onConfirm={handleConfirmVerify}
        isLoading={isVerifying}
      />
      <ConfirmModal
        open={isUnlinkConfirmOpen}
        textTitle={lineStrings.management.confirm.unlinkTitle}
        textMessage={`@${normalizeBasicId(basicId)}\n${lineStrings.management.confirm.unlinkDescription}`}
        textCancel={commonStrings.cancel}
        textConfirm={lineStrings.management.confirm.unlinkConfirm}
        confirmButtonVariant="outline"
        confirmButtonClassName="border-[#F35D5D] bg-[#F35D5D] text-white shadow-[0_16px_30px_rgba(243,93,93,0.32)] hover:border-[#E34E4E] hover:bg-[#E34E4E] hover:text-white hover:shadow-[0_20px_40px_rgba(243,93,93,0.38)] disabled:shadow-none"
        onClose={() => {
          if (isUnlinking) return;
          setIsUnlinkConfirmOpen(false);
        }}
        onConfirm={handleConfirmUnlink}
        isLoading={isUnlinking}
      />
      <Snackbar
        open={isReissueSnackbarOpen}
        message={
          reissueError ? reissueError : strings.form.actions.reissueSuccess
        }
        variant={reissueError ? 'error' : 'success'}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsReissueSnackbarOpen(false)}
      />
      <Snackbar
        open={verifySnackbar.open}
        message={
          verifySnackbar.success
            ? lineStrings.management.messages.verifySuccess
            : [lineStrings.management.messages.verifyFailed, verifySnackbar.detail].filter(Boolean).join(' ')
        }
        variant={verifySnackbar.success ? 'success' : 'error'}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setVerifySnackbar((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
}
